import React, { FormEvent, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { WhatsAppFAB } from '../components/WhatsAppFAB';
import { validateSAIdNumber, validateSAPhone, validateEmail } from '../utils/validation';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function crossValidateIdDob(idNumber: string, dob: string): string | null {
  if (!idNumber || idNumber.length < 6 || !dob) return null;

  // DOB is YYYY-MM-DD, ID prefix is YYMMDD
  const [yyyy, mm, dd] = dob.split('-');
  if (!yyyy || !mm || !dd) return null;

  const yy = yyyy.slice(2); // last 2 digits of year
  const expectedPrefix = `${yy}${mm}${dd}`;
  const idPrefix = idNumber.slice(0, 6);

  if (idPrefix !== expectedPrefix) {
    return `ID number does not match date of birth (expected ${expectedPrefix}XXXXXXX)`;
  }
  return null;
}

export function PublicRegisterPage() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    idNumber: '',
    phone: '',
    email: '',
    medicalAidName: '',
    medicalAidMemberNumber: '',
    reasonForVisit: '',
    popiaConsent: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const today = new Date().toISOString().split('T')[0];

  const updateField = useCallback((field: string, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      const next = { ...prev };
      delete next[field];
      // Clear cross-validation error when either field changes
      if (field === 'idNumber' || field === 'dateOfBirth') delete next.idDobMismatch;
      // Validate DOB not in future
      if (field === 'dateOfBirth' && typeof value === 'string' && value > new Date().toISOString().split('T')[0]) {
        next.dateOfBirth = 'Date of birth cannot be in the future';
      }
      return next;
    });
  }, []);

  const handleDobIdBlur = useCallback(() => {
    setErrors(prev => {
      const next = { ...prev };
      const id = form.idNumber.trim();

      // Step 1: Length check first
      if (id.length > 0 && id.length < 13) {
        next.idNumber = 'SA ID must be 13 digits';
        delete next.idDobMismatch;
        return next;
      }

      // Step 2: Full Luhn validation (only if 13 digits)
      if (id.length === 13) {
        const result = validateSAIdNumber(id);
        if (!result.valid) {
          next.idNumber = result.error!;
          delete next.idDobMismatch;
          return next;
        } else {
          delete next.idNumber;
        }

        // Step 3: DOB cross-validation
        if (form.dateOfBirth) {
          const msg = crossValidateIdDob(id, form.dateOfBirth);
          if (msg) {
            next.idDobMismatch = msg;
          } else {
            delete next.idDobMismatch;
          }
        }
      } else {
        delete next.idDobMismatch;
      }
      return next;
    });
  }, [form.idNumber, form.dateOfBirth]);

  const validate = useCallback((): Record<string, string> => {
    const errs: Record<string, string> = {};

    if (!form.firstName.trim()) errs.firstName = 'First name is required';
    if (!form.lastName.trim()) errs.lastName = 'Last name is required';
    if (!form.dateOfBirth) {
      errs.dateOfBirth = 'Date of birth is required';
    } else if (form.dateOfBirth > new Date().toISOString().split('T')[0]) {
      errs.dateOfBirth = 'Date of birth cannot be in the future';
    }

    if (!form.idNumber.trim()) {
      errs.idNumber = 'ID number is required';
    } else {
      const idResult = validateSAIdNumber(form.idNumber.trim());
      if (!idResult.valid) errs.idNumber = idResult.error!;
    }

    if (!form.phone.trim()) {
      errs.phone = 'Mobile number is required';
    } else if (!validateSAPhone(form.phone.trim()).valid) {
      errs.phone = 'Enter a valid SA mobile number (e.g. 0821234567)';
    }

    if (form.email && !validateEmail(form.email.trim()).valid) {
      errs.email = 'Enter a valid email address';
    }

    if (!errs.idNumber && !errs.dateOfBirth) {
      const mismatch = crossValidateIdDob(form.idNumber.trim(), form.dateOfBirth);
      if (mismatch) errs.idDobMismatch = mismatch;
    }

    // Medical aid cross-field validation
    if (form.medicalAidName.trim() && !form.medicalAidMemberNumber.trim()) {
      errs.medicalAidMemberNumber = 'Please enter your membership number';
    }
    if (form.medicalAidMemberNumber.trim() && !form.medicalAidName.trim()) {
      errs.medicalAidName = 'Please enter your medical aid name';
    }

    if (!form.reasonForVisit.trim()) errs.reasonForVisit = 'Reason for visit is required';
    if (!form.popiaConsent) errs.popiaConsent = 'POPIA consent is required to proceed';

    return errs;
  }, [form]);

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/pending-patients`, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        dateOfBirth: form.dateOfBirth,
        idNumber: form.idNumber.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        medicalAidName: form.medicalAidName.trim(),
        medicalAidMemberNumber: form.medicalAidMemberNumber.trim(),
        reasonForVisit: form.reasonForVisit.trim(),
        popiaConsent: form.popiaConsent,
      });
      setSubmitted(true);
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        const detail = err.response?.data?.detail;
        if (err.response?.status === 429 || err.response?.status === 409) {
          setSubmitError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again or contact the practice directly.');
        } else {
          setSubmitError('Something went wrong. Please try again or contact the practice directly.');
        }
      } else {
        setSubmitError('Something went wrong. Please try again or contact the practice directly.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [form, validate]);

  // Thank-you screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-lg shadow-md p-8">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank You</h1>
            <p className="text-gray-600 mb-6">
              Your registration has been submitted. Our practice team will review
              your details and be in touch. Please note that submitting this form does not confirm an appointment.
            </p>
            <Link
              to="/"
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Return to homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const inputClass = (field: string) =>
    `w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent ${
      errors[field]
        ? 'border-red-300 focus:ring-red-500'
        : 'border-gray-300 focus:ring-blue-500'
    }`;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-lg font-bold text-gray-900">
            IKhan Gynae
          </Link>
          <Link
            to="/login"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Staff Login
          </Link>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Patient Registration</h1>
          <p className="text-sm text-gray-600 mt-1">
            Complete this form to register as a new patient. Our team will review
            your information and contact you.
          </p>
        </div>

        {submitError && (
          <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">
            {submitError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                id="firstName"
                type="text"
                value={form.firstName}
                onChange={e => updateField('firstName', e.target.value)}
                className={inputClass('firstName')}
              />
              {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName}</p>}
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                id="lastName"
                type="text"
                value={form.lastName}
                onChange={e => updateField('lastName', e.target.value)}
                className={inputClass('lastName')}
              />
              {errors.lastName && <p className="mt-1 text-xs text-red-600">{errors.lastName}</p>}
            </div>
          </div>

          {/* DOB */}
          <div>
            <label htmlFor="dob" className="block text-sm font-medium text-gray-700 mb-1">
              Date of Birth *
            </label>
            <input
              id="dob"
              type="date"
              max={today}
              value={form.dateOfBirth}
              onChange={e => updateField('dateOfBirth', e.target.value)}
              onBlur={handleDobIdBlur}
              className={inputClass('dateOfBirth')}
            />
            {errors.dateOfBirth && <p className="mt-1 text-xs text-red-600">{errors.dateOfBirth}</p>}
          </div>

          {/* SA ID */}
          <div>
            <label htmlFor="idNumber" className="block text-sm font-medium text-gray-700 mb-1">
              SA ID Number *
            </label>
            <input
              id="idNumber"
              type="text"
              inputMode="numeric"
              maxLength={13}
              value={form.idNumber}
              onChange={e => updateField('idNumber', e.target.value.replace(/\D/g, ''))}
              onBlur={handleDobIdBlur}
              className={inputClass(errors.idDobMismatch ? 'idDobMismatch' : 'idNumber')}
              placeholder="13-digit ID number"
            />
            {errors.idNumber && <p className="mt-1 text-xs text-red-600">{errors.idNumber}</p>}
            {!errors.idNumber && errors.idDobMismatch && <p className="mt-1 text-xs text-red-600">{errors.idDobMismatch}</p>}
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Mobile Number *
            </label>
            <input
              id="phone"
              type="tel"
              value={form.phone}
              onChange={e => updateField('phone', e.target.value)}
              className={inputClass('phone')}
              placeholder="082 123 4567"
            />
            {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={form.email}
              onChange={e => updateField('email', e.target.value)}
              className={inputClass('email')}
            />
            {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
          </div>

          {/* Medical Aid */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="medAidName" className="block text-sm font-medium text-gray-700 mb-1">
                Medical Aid Name
              </label>
              <input
                id="medAidName"
                type="text"
                value={form.medicalAidName}
                onChange={e => updateField('medicalAidName', e.target.value)}
                onBlur={() => {
                  if (form.medicalAidMemberNumber.trim() && !form.medicalAidName.trim()) {
                    setErrors(prev => ({ ...prev, medicalAidName: 'Please enter your medical aid name' }));
                  }
                }}
                className={inputClass('medicalAidName')}
                placeholder="e.g. Discovery"
              />
              {errors.medicalAidName && <p className="mt-1 text-xs text-red-600">{errors.medicalAidName}</p>}
            </div>
            <div>
              <label htmlFor="medAidNum" className="block text-sm font-medium text-gray-700 mb-1">
                Membership Number
              </label>
              <input
                id="medAidNum"
                type="text"
                value={form.medicalAidMemberNumber}
                onChange={e => updateField('medicalAidMemberNumber', e.target.value)}
                onBlur={() => {
                  if (form.medicalAidName.trim() && !form.medicalAidMemberNumber.trim()) {
                    setErrors(prev => ({ ...prev, medicalAidMemberNumber: 'Please enter your membership number' }));
                  }
                }}
                className={inputClass('medicalAidMemberNumber')}
              />
              {errors.medicalAidMemberNumber && <p className="mt-1 text-xs text-red-600">{errors.medicalAidMemberNumber}</p>}
            </div>
          </div>

          {/* Reason */}
          <div>
            <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
              Reason for Visit *
            </label>
            <textarea
              id="reason"
              rows={3}
              maxLength={500}
              value={form.reasonForVisit}
              onChange={e => updateField('reasonForVisit', e.target.value)}
              className={inputClass('reasonForVisit')}
              placeholder="Briefly describe the reason for your visit"
            />
            <p className="mt-1 text-xs text-gray-400 text-right">{form.reasonForVisit.length}/500</p>
            {errors.reasonForVisit && <p className="mt-1 text-xs text-red-600">{errors.reasonForVisit}</p>}
          </div>

          {/* POPIA Consent */}
          <div className="bg-gray-50 rounded-md p-4 border border-gray-200">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.popiaConsent}
                onChange={e => updateField('popiaConsent', e.target.checked)}
                className="h-5 w-5 text-blue-600 rounded mt-0.5 flex-shrink-0"
              />
              <span className="text-sm text-gray-700">
                I consent to the collection and processing of my personal information
                by IKhan Gynae Practice in accordance with the Protection of Personal
                Information Act (POPIA). My information will be used solely for
                healthcare purposes and will not be shared with third parties without
                my explicit consent. *
              </span>
            </label>
            {errors.popiaConsent && <p className="mt-2 text-xs text-red-600">{errors.popiaConsent}</p>}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 px-4 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Submitting...
              </span>
            ) : (
              'Submit Registration'
            )}
          </button>
        </form>
      </main>
      <WhatsAppFAB />
    </div>
  );
}
